// Branding: name, logo, favicon and brand colour.
import { RotateCcw } from 'lucide-react'
import { ColorField, Field, FieldError, ImageDrop, LowContrast, Panel } from './SettingsPrimitives'
import { useLogoColors } from './logoPalette'
import { sameColor } from './settingsModel'

export default function BrandingSection({
  draft, setField, errors, disabled, uploading, onUpload, onRequestReset, resetting, bindSpot, logoUrl, logoSampleUrl,
}) {
  const logo = useLogoColors(logoUrl, logoSampleUrl)
  return (
    <div className="space-y-4">
      <Panel title="Name">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div {...bindSpot('name')}>
            <Field label="App name" htmlFor="settings-app-name" count={draft.app_name.length} max={100} error={errors.app_name}>
              <input
                id="settings-app-name"
                type="text"
                className={`form-input ${errors.app_name ? 'form-input-error' : ''}`}
                value={draft.app_name}
                onChange={e => setField('app_name', e.target.value)}
                maxLength={100}
                placeholder="Xtract"
                disabled={disabled}
              />
            </Field>
          </div>
          <div {...bindSpot('subtitle')}>
            <Field label="Subtitle" htmlFor="settings-subtitle" count={draft.subtitle.length} max={100}>
              <input
                id="settings-subtitle"
                type="text"
                className="form-input"
                value={draft.subtitle}
                onChange={e => setField('subtitle', e.target.value)}
                maxLength={100}
                placeholder="Synapx"
                disabled={disabled}
              />
            </Field>
          </div>
        </div>
      </Panel>

      <Panel title="Logo and favicon">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div {...bindSpot('logo')}>
            <ImageDrop
              label="Logo"
              hint="Square PNG or SVG"
              url={draft.logo_url}
              busy={uploading.logo}
              disabled={disabled}
              onFile={file => onUpload(file, 'logo')}
            />
          </div>
          <div {...bindSpot('favicon')}>
            <ImageDrop
              label="Favicon"
              hint="32 × 32 PNG"
              url={draft.favicon_url}
              busy={uploading.favicon}
              disabled={disabled}
              onFile={file => onUpload(file, 'favicon')}
              previewClassName="h-10 w-10"
            />
          </div>
        </div>
      </Panel>

      <Panel title="Brand colour" description="Colours the sidebar and accents across the portal.">
        <div {...bindSpot('brand')} className="flex flex-wrap items-center gap-3">
          <ColorField
            id="settings-brand-hex"
            size="lg"
            value={draft.primary_color}
            onChange={v => setField('primary_color', v)}
            invalid={Boolean(errors.primary_color)}
            disabled={disabled}
            ariaLabel="Brand colour"
          />
          <LowContrast fg="#ffffff" bg={draft.primary_color} />
          {logo.colors.length > 0 && !disabled && (
            <div className="flex items-center gap-1.5 sm:ml-auto">
              <span className="text-xs text-gray-500 dark:text-gray-400">From logo</span>
              {logo.colors.map(hex => (
                <button
                  key={hex}
                  type="button"
                  onClick={() => setField('primary_color', hex)}
                  title={`Use ${hex.toUpperCase()}`}
                  aria-label={`Use logo colour ${hex.toUpperCase()}`}
                  aria-pressed={sameColor(hex, draft.primary_color)}
                  className={`h-6 w-6 rounded-md ring-1 ring-inset ring-black/10 transition-transform hover:scale-110
                              focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500
                              ${sameColor(hex, draft.primary_color) ? 'outline outline-2 outline-offset-2 outline-navy-500' : ''}`}
                  style={{ backgroundColor: hex }}
                />
              ))}
            </div>
          )}
        </div>
        {errors.primary_color && <FieldError>{errors.primary_color}</FieldError>}
      </Panel>

      <section aria-labelledby="settings-danger-zone" className="rounded-xl border border-red-200 bg-red-50/40 dark:border-red-900/50 dark:bg-red-950/10">
        <h2 id="settings-danger-zone" className="border-b border-red-200/70 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-red-700
                                               dark:border-red-900/50 dark:text-red-400">
          Danger zone
        </h2>
        <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <RotateCcw className="mt-0.5 h-4 w-4 shrink-0 text-red-500 dark:text-red-400" />
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Reset branding</p>
              <p className="text-[13px] text-gray-500 dark:text-gray-400">
                Restores the default Xtract name, logo, colours and theme for everyone. Happens straight away.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onRequestReset}
            disabled={disabled || resetting}
            className="btn btn-sm shrink-0 border border-red-300 bg-white text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50
                       dark:border-red-800 dark:bg-transparent dark:text-red-400 dark:hover:bg-red-950/40"
          >
            Reset branding
          </button>
        </div>
      </section>
    </div>
  )
}
